import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Flag,
  MoreHorizontal,
  User,
  RefreshCw,
  Shield,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePatient } from '@/hooks/use-patients';
import { formatPhone, formatDate, calculateAge } from '@/lib/format';
import { getPatientClient, getIntegrationClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsTrigger } from '@/components/ui/tabs';
import { PageTabsList } from '@/components/custom/page-tabs-list';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { PatientInteractionList } from '@/components/interactions/PatientInteractionList';
import { PatientAppointmentList } from '@/components/appointments/PatientAppointmentList';
import { EhrSyncStatus } from '@/components/ehr/EhrSyncStatus';
import { patientEhrSyncState } from '@/lib/ehr-sync-state';
import { patientSyncToast } from './patient-sync-message';

/**
 * Hook to flag a patient.
 */
function useFlagPatientMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => getPatientClient().flagPatient(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.detail(id) });
    },
  });
}

/**
 * Hook to unflag a patient.
 */
function useUnflagPatientMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => getPatientClient().unflagPatient(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.detail(id) });
    },
  });
}

/**
 * Hook to fetch active integrations (for sync button).
 */
function useIntegrations() {
  return useQuery({
    queryKey: queryKeys.integrations.list(),
    queryFn: () => getIntegrationClient().getAll(),
  });
}

/**
 * Hook to sync a patient from EHR.
 */
function useSyncPatientMutation(patientId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      integrationId,
      ehrPatientId,
    }: {
      integrationId: string;
      ehrPatientId: string;
    }) => getIntegrationClient().syncPatient(integrationId, ehrPatientId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all });
      const { level, message } = patientSyncToast(result);
      toast[level](message);
    },
    // A failed sync is recorded on the patient too (ADR-284), so re-read it
    // either way to show the new status. The error toast is global.
    onSettled: () => {
      if (patientId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.patients.detail(patientId) });
      }
    },
  });
}

/**
 * Insurance info row component.
 */
function InsuranceField({
  label,
  value,
}: {
  label: string;
  value: string | undefined | null;
}): React.ReactElement | null {
  if (!value) return null;
  return (
    <div className="flex justify-between py-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground">{value}</span>
    </div>
  );
}

/**
 * Demographic row component for consistent styling.
 */
function DemographicRow({
  label,
  value,
  className,
}: {
  label: string;
  value: string | undefined | null;
  className?: string;
}): React.ReactElement | null {
  if (!value) return null;
  return (
    <div className={cn('flex justify-between py-2 border-b last:border-0', className)}>
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground">{value}</span>
    </div>
  );
}

export type PatientTab = 'information' | 'appointments' | 'insurance' | 'interactions';

const validTabs: PatientTab[] = ['information', 'appointments', 'insurance', 'interactions'];

/** The patient page: the patient and tab come from the URL. */
export default function PatientDetailPage(): React.ReactElement {
  const { id, tab } = useParams({ strict: false }) as { id: string; tab?: string };
  const navigate = useNavigate();

  // Validate and normalize tab value
  const currentTab: PatientTab = validTabs.includes(tab as PatientTab)
    ? (tab as PatientTab)
    : 'information';

  return (
    <PatientDetail
      patientId={id}
      tab={currentTab}
      onTabChange={(value) =>
        void navigate({ to: '/patients/$id/$tab', params: { id, tab: value }, replace: true })
      }
    />
  );
}

function BackToPatients(): React.ReactElement {
  return (
    <div className="mb-4">
      <Button
        variant="ghost"
        size="sm"
        asChild
        className="-ml-2 text-muted-foreground hover:text-foreground"
      >
        <Link to="/patients">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Patients
        </Link>
      </Button>
    </div>
  );
}

/**
 * One patient: header, actions and tabs. The page shows it under its own URL;
 * `PatientSheet` shows it in a slide-over, without the way back to the list.
 */
export function PatientDetail({
  patientId: id,
  tab: currentTab,
  onTabChange,
  showBack = true,
}: {
  patientId: string;
  tab: PatientTab;
  onTabChange: (tab: PatientTab) => void;
  /** "Back to Patients": the page has it, a slide-over over another page does not. */
  showBack?: boolean;
}): React.ReactElement {
  const { data: patient, isLoading, error } = usePatient(id);

  const handleTabChange = (value: string): void => {
    if (validTabs.includes(value as PatientTab)) onTabChange(value as PatientTab);
  };

  const flagPatient = useFlagPatientMutation();
  const unflagPatient = useUnflagPatientMutation();
  const syncPatient = useSyncPatientMutation(id);
  const { data: integrations } = useIntegrations();

  const isFlagged = patient?.flagged === 1;
  const activeIntegration = integrations?.find((i) => i.enabled);

  const handleFlagToggle = (): void => {
    if (!id) return;
    if (isFlagged) {
      unflagPatient.mutate(id);
    } else {
      flagPatient.mutate(id);
    }
  };

  if (error) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        {showBack && <BackToPatients />}
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-destructive">
          Failed to load patient details. Please try again.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        {showBack && <BackToPatients />}
        <div className="mb-6">
          <Skeleton className="h-10 w-[250px]" />
          <Skeleton className="mt-2 h-5 w-[180px]" />
        </div>
        <Skeleton className="h-[400px] w-full rounded-xl" />
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        {showBack && <BackToPatients />}
        <div className="text-center py-16 text-muted-foreground">Patient not found.</div>
      </div>
    );
  }

  const syncState = patientEhrSyncState(patient);
  const runSync =
    activeIntegration && patient.ehrPatientId
      ? (): void =>
          syncPatient.mutate({
            integrationId: activeIntegration.id,
            ehrPatientId: patient.ehrPatientId!,
          })
      : undefined;

  const contact = patient.cachedData?.contact?.data;
  const demographics = patient.cachedData?.demographics?.data;
  const age = patient.dateOfBirth ? calculateAge(patient.dateOfBirth) : null;

  // Get initials for avatar
  const initials = `${patient.firstName?.[0] ?? ''}${patient.lastName?.[0] ?? ''}`.toUpperCase();

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {showBack && <BackToPatients />}

      {/* Patient header */}
      <div className="mb-6 flex items-start justify-between">
        <div className="flex items-center gap-4">
          {/* Avatar */}
          <div
            className={cn(
              'w-16 h-16 rounded-full flex items-center justify-center text-xl font-semibold',
              isFlagged
                ? 'bg-destructive/10 text-destructive ring-2 ring-destructive/40'
                : 'bg-primary text-primary-foreground'
            )}
          >
            {initials}
          </div>

          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-foreground">
                {patient.firstName} {patient.lastName}
              </h1>
              {isFlagged && <Flag className="h-5 w-5 fill-destructive text-destructive" />}
            </div>
            <div className="flex items-center gap-3 mt-1">
              {patient.mrn && (
                <Badge variant="secondary" className="font-mono text-xs">
                  MRN: {patient.mrn}
                </Badge>
              )}
              {patient.ehrPatientId && (
                <Badge variant="secondary" className="font-mono text-xs">
                  EHR ID: {patient.ehrPatientId}
                </Badge>
              )}
              {patient.dateOfBirth && (
                <span className="text-sm text-muted-foreground">
                  {formatDate(patient.dateOfBirth)}
                  {age !== null && <span className="ml-1">({age} yrs)</span>}
                </span>
              )}
            </div>
            {syncState && (
              <EhrSyncStatus
                state={syncState}
                onResync={runSync}
                isResyncing={syncPatient.isPending}
                className="mt-2"
              />
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9"
                aria-label={syncPatient.isPending ? 'Syncing from EHR' : 'More actions'}
              >
                {syncPatient.isPending ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <MoreHorizontal className="h-4 w-4" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {runSync && (
                <DropdownMenuItem disabled={syncPatient.isPending} onClick={runSync}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {syncPatient.isPending ? 'Syncing...' : 'Sync EHR'}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={handleFlagToggle}
                disabled={flagPatient.isPending || unflagPatient.isPending}
                className={cn(!isFlagged && 'text-destructive')}
              >
                <Flag className={cn('mr-2 h-4 w-4', isFlagged && 'fill-current')} />
                {isFlagged ? 'Unflag' : 'Flag'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={currentTab} onValueChange={handleTabChange} className="w-full">
        <PageTabsList className="mb-4">
          <TabsTrigger value="information">Information</TabsTrigger>
          <TabsTrigger value="appointments">Appointments</TabsTrigger>
          <TabsTrigger value="insurance">Insurance</TabsTrigger>
          <TabsTrigger value="interactions">Interactions</TabsTrigger>
        </PageTabsList>

        <TabsContent value="information" className="mt-0">
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Contact Information */}
            <Card className="shadow-xs">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {patient.phone || patient.email || contact?.email || contact?.address ? (
                  <div className="space-y-3">
                    {patient.phone && (
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Phone</div>
                          <div className="text-sm font-medium">{formatPhone(patient.phone)}</div>
                        </div>
                      </div>
                    )}
                    {(patient.email || contact?.email) && (
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Email</div>
                          <div className="text-sm font-medium">
                            {patient.email || contact?.email}
                          </div>
                        </div>
                      </div>
                    )}
                    {contact?.address && (
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center mt-0.5">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Address</div>
                          <div className="text-sm font-medium">
                            {contact.address.line1 && <div>{contact.address.line1}</div>}
                            {contact.address.line2 && <div>{contact.address.line2}</div>}
                            {(contact.address.city ||
                              contact.address.state ||
                              contact.address.postalCode) && (
                              <div>
                                {contact.address.city}
                                {contact.address.city && contact.address.state && ', '}
                                {contact.address.state} {contact.address.postalCode}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    No contact information available
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Demographics */}
            <Card className="shadow-xs">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  Demographics
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-0">
                  <DemographicRow
                    label="Date of Birth"
                    value={
                      patient.dateOfBirth
                        ? `${formatDate(patient.dateOfBirth)}${age !== null ? ` (${age} years)` : ''}`
                        : null
                    }
                  />
                  <DemographicRow label="Preferred Name" value={patient.preferredName} />
                  <DemographicRow
                    label="Sex at Birth"
                    value={
                      demographics?.gender
                        ? demographics.gender.charAt(0).toUpperCase() + demographics.gender.slice(1)
                        : null
                    }
                  />
                  <DemographicRow
                    label="Preferred Sex"
                    value={
                      patient.preferredSex
                        ? patient.preferredSex.charAt(0).toUpperCase() +
                          patient.preferredSex.slice(1)
                        : null
                    }
                  />
                  <DemographicRow label="Race" value={demographics?.race} />
                  <DemographicRow label="Ethnicity" value={demographics?.ethnicity} />
                  <DemographicRow
                    label="Ethnic Background"
                    value={demographics?.ethnicBackground}
                  />
                  <DemographicRow
                    label="Preferred Language"
                    value={demographics?.preferredLanguage}
                  />
                  <DemographicRow label="Religion" value={demographics?.religion} />

                  {!patient.dateOfBirth &&
                    !patient.preferredName &&
                    !patient.preferredSex &&
                    !demographics?.gender &&
                    !demographics?.race &&
                    !demographics?.ethnicity &&
                    !demographics?.preferredLanguage &&
                    !demographics?.religion && (
                      <p className="text-sm text-muted-foreground italic py-2">
                        No demographic information available
                      </p>
                    )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="appointments" className="mt-0">
          <PatientAppointmentList patientId={id} />
        </TabsContent>

        <TabsContent value="insurance" className="mt-0">
          {(() => {
            const insurance = patient.cachedData?.insurance?.data;

            if (!insurance?.primary && !insurance?.secondary && !insurance?.tertiary) {
              return (
                <div className="py-12 text-center">
                  <Shield className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground">No insurance information available</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Insurance data will appear here after syncing from EHR
                  </p>
                </div>
              );
            }

            const formatSubscriberName = (
              coverage: NonNullable<typeof insurance>['primary']
            ): string | null => {
              if (!coverage?.subscriberFirstName && !coverage?.subscriberLastName) return null;
              const parts = [
                coverage.subscriberFirstName,
                coverage.subscriberMiddleName,
                coverage.subscriberLastName,
              ].filter(Boolean);
              return parts.join(' ') || null;
            };

            const renderCoverageCard = (
              coverage: NonNullable<typeof insurance>['primary'],
              title: string,
              iconColor: string
            ): React.ReactElement | null => {
              if (!coverage) return null;
              return (
                <Card className="shadow-xs">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <Shield className={`h-4 w-4 ${iconColor}`} />
                      {title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-0">
                      <InsuranceField label="Payer" value={coverage.payerName} />
                      <InsuranceField label="Payer ID" value={coverage.payerId} />
                      <InsuranceField label="Plan" value={coverage.planName} />
                      <InsuranceField label="Plan Type" value={coverage.planType} />
                      <InsuranceField label="Insured" value={formatSubscriberName(coverage)} />
                      <InsuranceField
                        label="Relationship"
                        value={
                          coverage.relationship
                            ? coverage.relationship.charAt(0).toUpperCase() +
                              coverage.relationship.slice(1)
                            : null
                        }
                      />
                      <InsuranceField label="Subscriber ID" value={coverage.subscriberId} />
                      <InsuranceField label="Member ID" value={coverage.memberId} />
                      <InsuranceField label="Group #" value={coverage.groupNumber} />
                      <InsuranceField label="Group Name" value={coverage.groupName} />
                      <InsuranceField label="Network" value={coverage.network} />
                      <InsuranceField
                        label="Coverage"
                        value={
                          coverage.coverageStart
                            ? `${formatDate(coverage.coverageStart)}${coverage.coverageEnd ? ` - ${formatDate(coverage.coverageEnd)}` : ' - Present'}`
                            : null
                        }
                      />
                      <InsuranceField
                        label="Copay"
                        value={coverage.copay != null ? `$${coverage.copay}` : null}
                      />
                      <InsuranceField
                        label="Deductible"
                        value={coverage.deductible != null ? `$${coverage.deductible}` : null}
                      />
                    </div>
                  </CardContent>
                </Card>
              );
            };

            return (
              <div className="grid gap-4 lg:grid-cols-2">
                {renderCoverageCard(insurance.primary, 'Primary Insurance', 'text-primary')}
                {renderCoverageCard(
                  insurance.secondary,
                  'Secondary Insurance',
                  'text-muted-foreground'
                )}
                {renderCoverageCard(
                  insurance.tertiary,
                  'Tertiary Insurance',
                  'text-muted-foreground/70'
                )}
              </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="interactions" className="mt-0">
          <PatientInteractionList patientId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
